import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { updateTeam } from '../../../services/teamService';
import { getTeamDeliverables } from '../../../services/deliverableService';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import TeamUploadModal from './TeamUploadModal';

const TeamDetailsAccordion = ({ team, isExpanded, onToggle, onDelete, onEdit, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedWorkSummary, setEditedWorkSummary] = useState(team?.workSummary || '');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [deliverables, setDeliverables] = useState([]);
  const [loadingDeliverables, setLoadingDeliverables] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [teamUploads, setTeamUploads] = useState([]);
  const [loadingTeamUploads, setLoadingTeamUploads] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Load deliverables when accordion expands
  useEffect(() => {
    if (isExpanded && team?.id) {
      loadDeliverables();
      loadTeamDocuments();
      loadTeamUploads();
      loadCurrentUserRole();
    }
  }, [isExpanded, team?.id]);

  const loadCurrentUserRole = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      
      setCurrentUserId(user?.id);
      
      const { data, error } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
      if (error) throw error;
      
      setCurrentUserRole(data?.role);
    } catch (err) {
      console.error('Error loading user role:', err);
    }
  };

  const loadTeamUploads = async () => {
    try {
      setLoadingTeamUploads(true);
      const { teamStageUploadsService } = await import('../../../services/teamStageUploadsService');
      const uploads = await teamStageUploadsService?.getTeamLevelUploads(team?.id);
      setTeamUploads(uploads || []);
    } catch (err) {
      console.error('Error loading team uploads:', err);
    } finally {
      setLoadingTeamUploads(false);
    }
  };

  const handleDeleteTeamUpload = async (uploadId, fileUrl) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const { teamStageUploadsService } = await import('../../../services/teamStageUploadsService');
      await teamStageUploadsService?.deleteUpload(uploadId, fileUrl);
      await loadTeamUploads();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error deleting upload:', err);
      alert('Failed to delete resource. Please try again.');
    }
  };

  const handleDownloadTeamUpload = async (upload) => {
    try {
      // Check if it's a link or file
      if (upload?.fileUrl?.startsWith('http://') || upload?.fileUrl?.startsWith('https://')) {
        // It's a link, open in new tab
        window.open(upload?.fileUrl, '_blank');
      } else {
        // It's a file, generate signed URL
        const { data, error } = await supabase?.storage
          ?.from('team-stage-files')
          ?.createSignedUrl(upload?.fileUrl, 60);
        
        if (error) throw error;
        
        // Open signed URL in new tab
        window.open(data?.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Error downloading upload:', err);
      alert('Failed to open resource. Please try again.');
    }
  };

  const canDeleteUpload = (upload) => {
    if (!currentUserRole) return false;
    // PCF Admin can delete any upload
    if (currentUserRole === 'pcf_admin') return true;
    // User can delete their own uploads
    return upload?.uploadedBy === currentUserId;
  };

  const loadDeliverables = async () => {
    try {
      setLoadingDeliverables(true);
      const data = await getTeamDeliverables(team?.id);
      setDeliverables(data || []);
    } catch (err) {
      console.error('Error loading deliverables:', err);
    } finally {
      setLoadingDeliverables(false);
    }
  };

  const loadTeamDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const { data, error } = await supabase?.from('team_deliverable_uploads')?.select(`
          *,
          uploader:user_profiles!team_deliverable_uploads_uploaded_by_fkey(full_name, email),
          deliverable:team_deliverables!team_deliverable_uploads_deliverable_id_fkey(deliverable_name)
        `)?.eq('team_id', team?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error loading team documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentClick = async (document) => {
    setSelectedDocument(document);
    setLoadingPreview(true);
    setDocumentPreview('');

    try {
      // Extract file content preview based on file type
      const fileType = document?.file_type?.toLowerCase() || '';
      
      if (fileType === 'pdf' || fileType === 'application/pdf') {
        setDocumentPreview('PDF preview not available. Click "Download" to view the full document.');
      } else if (fileType?.includes('image')) {
        setDocumentPreview(`<img src="${document?.file_url}" alt="${document?.file_name}" class="max-w-full h-auto" />`);
      } else if (fileType?.includes('text') || fileType === 'txt') {
        // For text files, try to fetch content
        try {
          const response = await fetch(document?.file_url);
          const text = await response?.text();
          setDocumentPreview(text?.substring(0, 1000) + (text?.length > 1000 ? '...' : ''));
        } catch (fetchErr) {
          setDocumentPreview('Preview not available. Click "Download" to view the full document.');
        }
      } else {
        setDocumentPreview('Preview not available for this file type. Click "Download" to view the full document.');
      }
    } catch (err) {
      console.error('Error loading document preview:', err);
      setDocumentPreview('Error loading preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setSelectedDocument(null);
    setDocumentPreview('');
  };

  const downloadDocument = async (document) => {
    try {
      // The file_url is stored as just the path (e.g., "teamId/deliverableId/timestamp_filename.ext")
      // No need to extract - use it directly
      const filePath = document?.file_url;

      // Generate signed URL for private bucket file
      const { data, error } = await supabase?.storage
        ?.from('team-stage-files') // Fixed: use correct bucket name
        ?.createSignedUrl(filePath, 60); // 60 seconds expiry

      if (error) throw error;

      // Create a temporary link and trigger download
      const link = window.document?.createElement('a');
      link.href = data?.signedUrl;
      link.download = document?.file_name || 'download';
      link.target = '_blank';
      window.document?.body?.appendChild(link);
      link?.click();
      window.document?.body?.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document. Please try again.');
    }
  };

  // Calculate overall progress
  const calculateProgress = () => {
    const stages = team?.stageRequirements || {};
    let totalRequired = 0;
    let totalCompleted = 0;

    Object.values(stages)?.forEach(stage => {
      totalRequired += stage?.required_uploads || 0;
      totalCompleted += stage?.completed_uploads || 0;
    });

    return totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;
  };

  const overallProgress = calculateProgress();

  // Handle work summary update
  const handleUpdateSummary = async () => {
    try {
      setUpdating(true);
      setError('');
      await updateTeam(team?.id, { workSummary: editedWorkSummary });
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating work summary:', err);
      setError(err?.message || 'Failed to update work summary');
    } finally {
      setUpdating(false);
    }
  };

  const stageLabels = {
    project_analysis: 'Project Analysis',
    solution_design: 'Solution Design',
    prototype_development: 'Prototype Development',
    mvp_development: 'MVP Development',
    final_presentation: 'Final Presentation'
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-soft">
      {/* Accordion Header */}
      <div
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <span className="text-lg font-bold text-primary">{team?.teamNumber}</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-foreground text-lg">{team?.teamName}</h4>
              <div className="flex items-center space-x-4 mt-1">
                <span className="text-sm text-muted-foreground flex items-center">
                  <Icon name="Users" size={14} className="mr-1" />
                  {team?.currentMembers || 0} members
                </span>
                {team?.category && (
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Icon name="Tag" size={14} className="mr-1" />
                    {team?.category}
                  </span>
                )}
                {team?.city && (
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Icon name="MapPin" size={14} className="mr-1" />
                    {team?.city}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right mr-4">
              <div className="text-sm font-medium text-muted-foreground">Progress</div>
              <div className="text-xl font-bold text-primary">{overallProgress}%</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                setShowUploadModal(true);
              }}
              className="mr-2"
            >
              <Icon name="Upload" size={16} className="mr-1" />
              Upload
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e?.stopPropagation();
                  onEdit();
                }}
                className="mr-2"
              >
                <Icon name="Edit" size={16} className="mr-1" />
                Edit
              </Button>
            )}
            <Icon
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              className="text-muted-foreground"
            />
          </div>
        </div>
      </div>
      {/* Accordion Content */}
      {isExpanded && (
        <div className="p-4 sm:p-6 border-t border-border bg-muted/20">
          {/* Team Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
              <h5 className="text-sm sm:text-base font-semibold text-foreground mb-3">Team Information</h5>
              <div className="space-y-2">
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">Team Number:</span>
                  <p className="text-sm sm:text-base font-medium text-foreground">{team?.teamNumber}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">Mentor:</span>
                  <p className="text-sm sm:text-base font-medium text-foreground">{team?.mentor || 'Not assigned'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">City:</span>
                  <p className="text-sm sm:text-base font-medium text-foreground">{team?.city || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">Category:</span>
                  <p className="text-sm sm:text-base font-medium text-foreground">{team?.category || 'Not specified'}</p>
                </div>
                {team?.finalDueDate && (
                  <div>
                    <span className="text-xs sm:text-sm text-muted-foreground">Final Due Date:</span>
                    <p className="text-sm sm:text-base font-medium text-foreground">
                      {format(new Date(team.finalDueDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h5 className="text-sm sm:text-base font-semibold text-foreground mb-3">Problem Statement</h5>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {team?.problemStatement || 'No problem statement provided'}
              </p>
            </div>
          </div>

          {/* Stage Requirements */}
          {/*<div className="mb-6">
            <h5 className="font-semibold text-foreground mb-3">Stage Upload Requirements</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {Object.entries(team?.stageRequirements || {})?.map(([key, stage]) => {
                const progress = stage?.required_uploads > 0 
                  ? (stage?.completed_uploads / stage?.required_uploads) * 100 
                  : 0;
                
                return (
                  <div key={key} className="bg-card border border-border rounded-lg p-3">
                    <h6 className="text-sm font-medium text-foreground mb-2">
                      {stageLabels?.[key]}
                    </h6>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">
                        {stage?.completed_uploads || 0} / {stage?.required_uploads || 0}
                      </span>
                      <span className="text-xs font-medium text-primary">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary rounded-full h-2 transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>*/}

          {/* Deliverable Upload Progress */}
          {deliverables?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-foreground mb-3">Deliverable Upload Progress</h5>
              {loadingDeliverables ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader" size={24} className="text-primary animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {deliverables?.map(deliverable => {
                    const progress = deliverable?.requiredUploadCount > 0
                      ? (deliverable?.completedUploadCount / deliverable?.requiredUploadCount) * 100
                      : 0;
                    
                    const statusColors = {
                      not_started: 'text-muted-foreground',
                      in_progress: 'text-warning',
                      completed: 'text-success'
                    };

                    const statusLabels = {
                      not_started: 'Not Started',
                      in_progress: 'In Progress',
                      completed: 'Completed'
                    };

                    return (
                      <div key={deliverable?.id} className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h6 className="text-sm font-medium text-foreground mb-1">
                              {deliverable?.deliverableName}
                            </h6>
                            {deliverable?.dueDate && (
                              <p className="text-xs text-muted-foreground">
                                Due: {format(new Date(deliverable?.dueDate), 'MMM dd, yyyy')}
                              </p>
                            )}
                          </div>
                          <span className={`text-xs font-medium ${statusColors?.[deliverable?.status]}`}>
                            {statusLabels?.[deliverable?.status]}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-muted-foreground">
                            {deliverable?.completedUploadCount || 0} / {deliverable?.requiredUploadCount || 0} files uploaded
                          </span>
                          <span className="text-xs font-medium text-primary">
                            {Math.round(progress)}%
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Work Summary */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-semibold text-foreground">Work Summary</h5>
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              )}
            </div>
            {isEditing ? (
              <div>
                <textarea
                  className="w-full min-h-[100px] p-3 rounded-lg border border-border bg-background text-foreground resize-y"
                  value={editedWorkSummary}
                  onChange={(e) => setEditedWorkSummary(e?.target?.value)}
                  placeholder="Enter work summary..."
                />
                {error && (
                  <p className="text-error text-sm mt-2">{error}</p>
                )}
                <div className="flex items-center space-x-2 mt-3">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleUpdateSummary}
                    disabled={updating}
                    iconName={updating ? 'Loader' : 'Check'}
                    iconPosition="left"
                  >
                    {updating ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedWorkSummary(team?.workSummary || '');
                      setError('');
                    }}
                    disabled={updating}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground leading-relaxed bg-muted/30 p-3 rounded-lg">
                {team?.workSummary || 'No work summary available'}
              </p>
            )}
          </div>

          {/* Team Members */}
          {team?.members?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-foreground mb-3">Team Members</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {team?.members?.map(member => (
                  <div key={member?.id} className="flex items-center space-x-3 p-3 bg-card border border-border rounded-lg">
                    <div className="flex-shrink-0">
                      {member?.avatarUrl ? (
                        <img
                          src={member?.avatarUrl}
                          alt={member?.fullName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={20} className="text-primary" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{member?.fullName}</p>
                      <p className="text-sm text-muted-foreground truncate">{member?.email}</p>
                    </div>
                    {member?.isLeader && (
                      <span className="flex-shrink-0 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                        Leader
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={onDelete}
              className="text-error hover:bg-error/10 hover:border-error/20"
            >
              Delete Team
            </Button>
          </div>

          {/* Team Documents Section */}
          {documents?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <h5 className="font-semibold text-foreground mb-3">Team Documents ({documents?.length})</h5>
              {loadingDocuments ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader" size={24} className="text-primary animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {documents?.map(doc => (
                    <div
                      key={doc?.id}
                      className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => handleDocumentClick(doc)}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <Icon name="FileText" size={20} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{doc?.file_name}</p>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {doc?.deliverable?.deliverable_name || 'Unknown deliverable'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Uploaded by {doc?.uploader?.full_name || 'Unknown'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(doc?.created_at), 'MMM dd, yyyy')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            downloadDocument(doc);
                          }}
                        >
                          <Icon name="Download" size={16} />
                        </Button>
                        <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Additional Resources Section */}
          <div className="mt-6 pt-6 border-t border-border">
            <h5 className="font-semibold text-foreground mb-3">Additional Resources</h5>
            <p className="text-xs text-muted-foreground mb-4">Resources uploaded by PCF Admin or City Official</p>
            
            {loadingTeamUploads ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader" size={24} className="text-primary animate-spin" />
              </div>
            ) : teamUploads?.length === 0 ? (
              <div className="text-center py-8 bg-muted/30 rounded-lg">
                <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No additional resources uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamUploads?.map((upload) => {
                  const canDelete = canDeleteUpload(upload);
                  return (
                    <div
                      key={upload?.id}
                      className="bg-muted/30 border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon 
                              name={upload?.fileUrl?.startsWith('http') ? 'Link' : 'FileText'} 
                              size={16} 
                              className="text-primary" 
                            />
                            <h4 className="font-semibold text-foreground">{upload?.notes || 'Untitled Resource'}</h4>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center">
                              <Icon name="User" size={12} className="mr-1" />
                              {upload?.uploaderName}
                            </span>
                            <span className="flex items-center">
                              <Icon name="Calendar" size={12} className="mr-1" />
                              {format(new Date(upload?.uploadedAt), 'MMM dd, yyyy')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadTeamUpload(upload)}
                          >
                            <Icon name="ExternalLink" size={14} className="mr-1" />
                            Open
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTeamUpload(upload?.id, upload?.fileUrl)}
                              className="text-error hover:bg-error/10"
                            >
                              <Icon name="Trash2" size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Document Preview Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground truncate">{selectedDocument?.file_name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDocument?.deliverable?.deliverable_name} • Uploaded by {selectedDocument?.uploader?.full_name}
                </p>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => downloadDocument(selectedDocument)}
                >
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePreview}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={32} className="text-primary animate-spin" />
                </div>
              ) : (
                <div className="prose max-w-none">
                  {documentPreview?.startsWith('<img') ? (
                    <div dangerouslySetInnerHTML={{ __html: documentPreview }} />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-foreground bg-muted/30 p-4 rounded-lg">
                      {documentPreview}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <TeamUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        teamId={team?.id}
        teamName={team?.teamName}
        onSuccess={() => {
          loadTeamDocuments();
          if (onUpdate) onUpdate();
        }}
      />
    </div>
  );
};

export default TeamDetailsAccordion;